export interface FontFaceSrcInput {
  url?: string;
  format?: 'woff2' | 'woff';
  local?: string;
}

export interface FontFaceInput {
  family?: string;
  src?: readonly FontFaceSrcInput[];
  weight?: number | string;
  style?: 'normal' | 'italic' | 'oblique';
  display?: 'auto' | 'block' | 'swap' | 'fallback' | 'optional';
  unicodeRange?: string;
  sizeAdjust?: string;
}

export interface FontSourcesInput {
  faces?: readonly FontFaceInput[] | null;
  stylesheets?: readonly string[] | null;
}

export interface FontPreconnect {
  href: string;
  crossOrigin?: 'anonymous';
}

export type DroppedFontFaceField =
  | 'family'
  | 'src'
  | 'weight'
  | 'style'
  | 'display'
  | 'unicodeRange'
  | 'sizeAdjust';

export type DroppedFontSource =
  | {
      source: 'face';
      index: number;
      field: DroppedFontFaceField;
      family?: string;
    }
  | { source: 'stylesheet'; index: number; field: 'url' };

export interface EmittedFontSources {
  css: string;
  stylesheets: string[];
  preconnects: FontPreconnect[];
  dropped: DroppedFontSource[];
}

export interface FontPreloadCandidate {
  href: string;
  type: 'font/woff2';
}

export const FONT_STYLESHEET_HOSTS: Readonly<
  Record<string, { fontOrigin: string }>
> = {
  'fonts.googleapis.com': { fontOrigin: 'https://fonts.gstatic.com' },
};

export const FONT_FACE_LIMIT = 32;
export const FONT_FACE_SRC_LIMIT = 16;
export const FONT_STYLESHEET_LIMIT = 8;
const FONT_FAMILY_MAX_LENGTH = 100;
const FONT_URL_MAX_LENGTH = 256;
const FONT_STYLESHEET_MAX_LENGTH = 2048;
const FONT_UNICODE_RANGE_MAX_LENGTH = 256;

const CONTROL_CHARS = '\\u0000-\\u001f\\u007f';
const UNSAFE_FONT_TOKEN = new RegExp(`["';{}<>\\\\${CONTROL_CHARS}]`);
const UNSAFE_URL_CHAR = new RegExp(`["\\\\<>${CONTROL_CHARS}]`);
const WEIGHT_TOKEN = /^(?:[1-9]\d{0,2}|1000)$/;
const UNICODE_RANGE_SEPARATOR = /, ?/;
const UNICODE_RANGE_EXPLICIT =
  /^U\+([0-9A-Fa-f]{1,6})(?:-([0-9A-Fa-f]{1,6}))?$/;
const UNICODE_RANGE_WILDCARD = /^U\+([0-9A-Fa-f]{0,5}\?{1,6})$/;
const MAX_CODE_POINT = 0x10ffff;
const SIZE_ADJUST = /^(?:[1-9]\d?|1\d\d|200)%$/;
const STYLES = ['normal', 'italic', 'oblique'] as const;
const DISPLAYS = ['auto', 'block', 'swap', 'fallback', 'optional'] as const;
const PRIMARY_SUBSET_CODE_POINT = 0x41;
const REGULAR_WEIGHT = 400;

interface SafeSrc {
  css: string;
  url?: string;
  format?: 'woff2' | 'woff';
}

interface SafeFace {
  family: string;
  src: SafeSrc[];
  weight?: string;
  style?: (typeof STYLES)[number];
  display: (typeof DISPLAYS)[number];
  unicodeRange?: string;
  codePoints?: CodePointRange[];
  sizeAdjust?: string;
}

function isFamilyName(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.length <= FONT_FAMILY_MAX_LENGTH &&
    !UNSAFE_FONT_TOKEN.test(value)
  );
}

function safeHttpsUrl(value: unknown, maxLength: number): URL | undefined {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    value.length > maxLength
  ) {
    return undefined;
  }
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return undefined;
  }
  if (
    parsed.protocol !== 'https:' ||
    parsed.username !== '' ||
    parsed.password !== '' ||
    UNSAFE_URL_CHAR.test(parsed.href)
  ) {
    return undefined;
  }
  return parsed;
}

function normalizeWeight(value: unknown): string | undefined | null {
  if (value === undefined) return undefined;
  if (typeof value === 'number') {
    return Number.isInteger(value) && value >= 1 && value <= 1000
      ? String(value)
      : null;
  }
  if (typeof value !== 'string') return null;
  const parts = value.trim().split(' ');
  if (parts.length !== 1 && parts.length !== 2) return null;
  if (!parts.every((part) => WEIGHT_TOKEN.test(part))) return null;
  if (parts.length === 2 && Number(parts[0]) > Number(parts[1])) return null;
  return parts.join(' ');
}

interface CodePointRange {
  low: number;
  high: number;
}

function parseUnicodeRangeToken(token: string): CodePointRange | undefined {
  let low: number;
  let high: number;
  const wildcard = UNICODE_RANGE_WILDCARD.exec(token);
  if (wildcard) {
    if (wildcard[1].length > 6) return undefined;
    low = parseInt(wildcard[1].replace(/\?/g, '0'), 16);
    high = parseInt(wildcard[1].replace(/\?/g, 'F'), 16);
  } else {
    const explicit = UNICODE_RANGE_EXPLICIT.exec(token);
    if (!explicit) return undefined;
    low = parseInt(explicit[1], 16);
    high = parseInt(explicit[2] ?? explicit[1], 16);
  }
  if (low > high || high > MAX_CODE_POINT) return undefined;
  return { low, high };
}

function parseUnicodeRange(value: unknown): CodePointRange[] | undefined {
  if (
    typeof value !== 'string' ||
    value.length > FONT_UNICODE_RANGE_MAX_LENGTH
  ) {
    return undefined;
  }
  const ranges: CodePointRange[] = [];
  for (const token of value.split(UNICODE_RANGE_SEPARATOR)) {
    const range = parseUnicodeRangeToken(token);
    if (!range) return undefined;
    ranges.push(range);
  }
  return ranges;
}

function isOneOf<T extends string>(
  values: readonly T[],
  value: unknown,
): value is T {
  return (
    typeof value === 'string' && (values as readonly string[]).includes(value)
  );
}

function toSafeSrc(src: FontFaceSrcInput): SafeSrc | undefined {
  if (!src || typeof src !== 'object') return undefined;
  if (src.local !== undefined) {
    if (src.url !== undefined || src.format !== undefined) return undefined;
    if (!isFamilyName(src.local) || /[()]/.test(src.local)) return undefined;
    return { css: `local("${src.local.trim()}")` };
  }
  if (src.format !== 'woff2' && src.format !== 'woff') return undefined;
  const url = safeHttpsUrl(src.url, FONT_URL_MAX_LENGTH);
  if (!url) return undefined;
  return {
    css: `url("${url.href}") format("${src.format}")`,
    url: url.href,
    format: src.format,
  };
}

function toSafeFace(face: FontFaceInput): SafeFace | DroppedFontFaceField {
  if (!face || typeof face !== 'object' || !isFamilyName(face.family)) {
    return 'family';
  }
  if (!Array.isArray(face.src)) return 'src';

  const weight = normalizeWeight(face.weight);
  if (weight === null) return 'weight';
  if (face.style !== undefined && !isOneOf(STYLES, face.style)) {
    return 'style';
  }
  if (face.display !== undefined && !isOneOf(DISPLAYS, face.display)) {
    return 'display';
  }
  const codePoints =
    face.unicodeRange === undefined
      ? undefined
      : parseUnicodeRange(face.unicodeRange);
  if (face.unicodeRange !== undefined && !codePoints) return 'unicodeRange';
  if (
    face.sizeAdjust !== undefined &&
    (typeof face.sizeAdjust !== 'string' || !SIZE_ADJUST.test(face.sizeAdjust))
  ) {
    return 'sizeAdjust';
  }

  const src = face.src
    .slice(0, FONT_FACE_SRC_LIMIT)
    .map(toSafeSrc)
    .filter((entry): entry is SafeSrc => !!entry);
  if (src.length === 0) return 'src';

  return {
    family: face.family.trim(),
    src,
    weight,
    style: face.style,
    display: face.display ?? 'swap',
    unicodeRange: face.unicodeRange,
    codePoints,
    sizeAdjust: face.sizeAdjust,
  };
}

function weightLowerBound(weight: string | undefined): number {
  return weight ? Number(weight.split(' ')[0]) : REGULAR_WEIGHT;
}

function compareText(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

function compareFaces(a: SafeFace, b: SafeFace): number {
  return (
    compareText(a.family.toLowerCase(), b.family.toLowerCase()) ||
    compareText(a.style ?? 'normal', b.style ?? 'normal') ||
    weightLowerBound(a.weight) - weightLowerBound(b.weight) ||
    compareText(a.unicodeRange ?? '', b.unicodeRange ?? '') ||
    compareText(faceToCSS(a), faceToCSS(b))
  );
}

function safeFaces(faces: readonly FontFaceInput[] | null | undefined): {
  faces: SafeFace[];
  dropped: DroppedFontSource[];
} {
  if (!Array.isArray(faces)) return { faces: [], dropped: [] };
  const safe: SafeFace[] = [];
  const dropped: DroppedFontSource[] = [];
  faces.slice(0, FONT_FACE_LIMIT).forEach((face, index) => {
    const result = toSafeFace(face);
    if (typeof result !== 'string') {
      safe.push(result);
    } else if (result === 'family') {
      dropped.push({ source: 'face', index, field: result });
    } else {
      dropped.push({
        source: 'face',
        index,
        field: result,
        family: face.family?.trim(),
      });
    }
  });
  return { faces: safe.sort(compareFaces), dropped };
}

function faceToCSS(face: SafeFace): string {
  const lines = [
    `  font-family: "${face.family}";`,
    `  src: ${face.src.map((entry) => entry.css).join(', ')};`,
  ];
  if (face.style) lines.push(`  font-style: ${face.style};`);
  if (face.weight) lines.push(`  font-weight: ${face.weight};`);
  lines.push(`  font-display: ${face.display};`);
  if (face.unicodeRange) lines.push(`  unicode-range: ${face.unicodeRange};`);
  if (face.sizeAdjust) lines.push(`  size-adjust: ${face.sizeAdjust};`);
  return `@font-face {\n${lines.join('\n')}\n}`;
}

function safeStylesheets(stylesheets: readonly string[] | null | undefined): {
  urls: URL[];
  dropped: DroppedFontSource[];
} {
  if (!Array.isArray(stylesheets)) return { urls: [], dropped: [] };
  const seen = new Set<string>();
  const urls: URL[] = [];
  const dropped: DroppedFontSource[] = [];
  stylesheets.slice(0, FONT_STYLESHEET_LIMIT).forEach((value, index) => {
    const url = safeHttpsUrl(value, FONT_STYLESHEET_MAX_LENGTH);
    if (
      !url ||
      url.port !== '' ||
      !Object.prototype.hasOwnProperty.call(FONT_STYLESHEET_HOSTS, url.hostname)
    ) {
      dropped.push({ source: 'stylesheet', index, field: 'url' });
      return;
    }
    if (seen.has(url.href)) return;
    seen.add(url.href);
    urls.push(url);
  });
  return { urls, dropped };
}

function stylesheetPreconnects(urls: readonly URL[]): FontPreconnect[] {
  const preconnects: FontPreconnect[] = [];
  const seen = new Set<string>();
  for (const url of urls) {
    const fontOrigin = FONT_STYLESHEET_HOSTS[url.hostname]?.fontOrigin;
    const hints: FontPreconnect[] = [{ href: url.origin }];
    if (fontOrigin) hints.push({ href: fontOrigin, crossOrigin: 'anonymous' });
    for (const hint of hints) {
      if (seen.has(hint.href)) continue;
      seen.add(hint.href);
      preconnects.push(hint);
    }
  }
  return preconnects;
}

/**
 * Converts Theme v2 `font.faces` and `font.stylesheets` into `@font-face` CSS,
 * stylesheet hrefs, and the preconnect origins those stylesheets need.
 *
 * Inputs are re-validated with the tenant-config write-side rules: faces with an
 * unsafe family or invalid descriptor are dropped, src entries must be `https:`
 * URLs or plain `local()` names, and stylesheets must be on an allowlisted host.
 * Faces default to `font-display: swap` and are emitted in a stable sort order.
 * Every face or stylesheet that was rejected is listed in `dropped` with its
 * input index and the field that failed, so callers can log it.
 *
 * @param font - The theme `font` object, or just its `faces` / `stylesheets`
 * @returns Empty `css` and empty lists when nothing valid is declared
 */
export function emitFontSources(
  font: FontSourcesInput | null | undefined,
): EmittedFontSources {
  const faces = safeFaces(font?.faces);
  const stylesheets = safeStylesheets(font?.stylesheets);
  return {
    css: faces.faces.map(faceToCSS).join('\n'),
    stylesheets: stylesheets.urls.map((url) => url.href),
    preconnects: stylesheetPreconnects(stylesheets.urls),
    dropped: [...faces.dropped, ...stylesheets.dropped],
  };
}

function primaryFamilyName(family: string): string | undefined {
  const stack = family.trim();
  const quote = stack[0];
  let first: string;
  if (quote === '"' || quote === "'") {
    const end = stack.indexOf(quote, 1);
    if (end === -1) return undefined;
    first = stack.slice(1, end);
  } else {
    first = stack.split(',')[0];
  }
  first = first.trim();
  return first ? first.toLowerCase() : undefined;
}

function coversRegularWeight(weight: string | undefined): boolean {
  if (!weight) return true;
  const [min, max = min] = weight.split(' ').map(Number);
  return min <= REGULAR_WEIGHT && REGULAR_WEIGHT <= max;
}

function coversPrimarySubset(
  codePoints: readonly CodePointRange[] | undefined,
): boolean {
  if (!codePoints) return true;
  return codePoints.some(
    ({ low, high }) =>
      low <= PRIMARY_SUBSET_CODE_POINT && PRIMARY_SUBSET_CODE_POINT <= high,
  );
}

function isStaticRegularWeight(weight: string | undefined): boolean {
  return !weight || weight === String(REGULAR_WEIGHT);
}

/**
 * Picks the faces worth preloading: normal style, covering weight 400, covering
 * Basic Latin, whose first URL source is woff2 (the file the browser fetches).
 * Each candidate's href is that URL exactly as `emitFontSources` writes it, so
 * the preload is reused. Static 400 faces rank ahead of variable-weight faces.
 *
 * @param faces - The theme `font.faces`
 * @param family - Optional family or CSS font stack; only its first entry is matched
 * @returns Best candidate first; callers wanting a single preload take the first
 */
export function fontPreloadCandidates(
  faces: readonly FontFaceInput[] | null | undefined,
  family?: string | null,
): FontPreloadCandidate[] {
  const wanted = family == null ? undefined : primaryFamilyName(family);
  if (family != null && !wanted) return [];

  const matches = safeFaces(faces).faces.filter(
    (face) =>
      (!wanted || face.family.toLowerCase() === wanted) &&
      (!face.style || face.style === 'normal') &&
      coversRegularWeight(face.weight) &&
      coversPrimarySubset(face.codePoints),
  );
  const ranked = [
    ...matches.filter((face) => isStaticRegularWeight(face.weight)),
    ...matches.filter((face) => !isStaticRegularWeight(face.weight)),
  ];

  return ranked.flatMap((face) => {
    const fetched = face.src.find((entry) => entry.url);
    return fetched?.url && fetched.format === 'woff2'
      ? [{ href: fetched.url, type: 'font/woff2' as const }]
      : [];
  });
}
